import {
  Package, Buildings, Truck, Scales, Factory, SealCheck, Flask,
  DoorOpen, SignOut, CheckCircle, XCircle, FileText, ShieldCheck,
  ArrowClockwise,
} from "@phosphor-icons/react";

/**
 * Configuration mapping antara `stage` (yang dikirim backend) → icon, warna,
 * dan label default. Dipakai bersama oleh Delivery Timeline & Receiving Timeline.
 */
export const STAGE_CFG = {
  // Common
  created:         { icon: Package,      color: "sky",     label: "Dibuat" },
  approved:        { icon: CheckCircle,  color: "emerald", label: "Disetujui" },
  rejected:        { icon: XCircle,      color: "red",     label: "Ditolak" },
  gate_in:         { icon: DoorOpen,     color: "navy",    label: "Masuk Gerbang" },
  weigh_in:        { icon: Scales,       color: "amber",   label: "Timbang Masuk" },
  weigh_out:       { icon: Scales,       color: "amber",   label: "Timbang Keluar" },
  gate_out:        { icon: SignOut,      color: "slate",   label: "Keluar Gerbang" },
  closed:          { icon: CheckCircle,  color: "emerald", label: "Selesai" },
  // Delivery-specific
  loading_start:   { icon: Factory,      color: "orange",  label: "Loading Mulai" },
  loading_finish:  { icon: SealCheck,    color: "emerald", label: "Loading Selesai" },
  // Receiving-specific
  lab_input:       { icon: Flask,        color: "purple",  label: "Hasil Lab Diinput" },
  spb_issued:      { icon: FileText,     color: "amber",   label: "SPB Diterbitkan" },
  spb_approved:    { icon: ShieldCheck,  color: "emerald", label: "SPB Disetujui" },
  spb_rejected:    { icon: XCircle,      color: "red",     label: "SPB Ditolak" },
  retur_marker:    { icon: ArrowClockwise, color: "orange", label: "Barang Retur" },
  force_closed:    { icon: ShieldCheck,  color: "red",     label: "Force-Closed (Admin)" },
};

export const COLOR_MAP = {
  sky:     { bg: "bg-sky-100",     ring: "ring-sky-300",     text: "text-sky-700",     dot: "bg-sky-500" },
  emerald: { bg: "bg-emerald-100", ring: "ring-emerald-300", text: "text-emerald-700", dot: "bg-emerald-500" },
  navy:    { bg: "bg-slate-800",   ring: "ring-slate-500",   text: "text-white",       dot: "bg-slate-800" },
  amber:   { bg: "bg-amber-100",   ring: "ring-amber-300",   text: "text-amber-700",   dot: "bg-amber-500" },
  orange:  { bg: "bg-orange-100",  ring: "ring-orange-300",  text: "text-orange-700",  dot: "bg-orange-500" },
  red:     { bg: "bg-red-100",     ring: "ring-red-300",     text: "text-red-700",     dot: "bg-red-500" },
  slate:   { bg: "bg-slate-100",   ring: "ring-slate-300",   text: "text-slate-700",   dot: "bg-slate-500" },
  purple:  { bg: "bg-purple-100",  ring: "ring-purple-300",  text: "text-purple-700",  dot: "bg-purple-500" },
};

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "-";
const fmtNum = (n) => (n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

/**
 * Timeline vertikal reusable.
 *
 * @param props.header  ReactNode — kartu ringkasan di atas timeline (do/rcv info).
 * @param props.events  Array of {stage, label, at, actor, meta}.
 */
export default function TimelineView({ header, events }) {
  return (
    <div>
      {header}
      <div className="p-5">
        {(!events || events.length === 0) ? (
          <div className="py-12 text-center text-xs text-slate-400">Belum ada event.</div>
        ) : (
          <ol className="relative" data-testid="timeline-events">
            <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-slate-200" />
            {events.map((ev, idx) => {
              const cfg = STAGE_CFG[ev.stage] || { icon: Package, color: "slate", label: ev.stage };
              const c = COLOR_MAP[cfg.color] || COLOR_MAP.slate;
              const Icon = cfg.icon;
              return (
                <li key={idx} className="relative pl-12 pb-6 last:pb-0" data-testid={`timeline-event-${ev.stage}-${idx}`}>
                  <div className={`absolute left-0 top-0 w-8 h-8 rounded-full ${c.bg} ${c.text} ring-2 ring-white flex items-center justify-center shadow-sm`}>
                    <Icon size={16} weight="fill" />
                  </div>
                  <div className="pt-0.5">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="font-heading font-bold text-[13px] text-slate-800">{ev.label || cfg.label}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{fmtDate(ev.at)}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      oleh <span className="font-semibold text-slate-700">{ev.actor || "-"}</span>
                    </div>
                    {ev.meta && Object.keys(ev.meta).length > 0 && (
                      <div className="mt-2 rounded-md bg-slate-50 border border-slate-100 px-3 py-2 text-[11px] text-slate-600 space-y-0.5">
                        {Object.entries(ev.meta).map(([k, v]) => {
                          if (v === undefined || v === null || v === "") return null;
                          const val = Array.isArray(v) ? v.join(", ") : (typeof v === "number" ? fmtNum(v) : String(v));
                          return (
                            <div key={k} className="flex gap-2">
                              <span className="text-[9px] uppercase tracking-wider text-slate-400 shrink-0 w-24 pt-0.5">{k.replace(/_/g, " ")}</span>
                              <span className="text-slate-700 font-mono break-all">{val}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

/** Small stat item untuk header card timeline. */
export function MiniStat({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-[13px] font-semibold text-slate-800 ${mono ? "font-mono" : ""} truncate`}>{value}</div>
    </div>
  );
}

export { fmtDate, fmtNum };
